import React, { useState, useMemo } from 'react';
import { Button } from './button';
import { Input } from './input';
import { Loader2, Check, X, Info, AlertTriangle, Frame as FrameIcon, ChevronRight, MapPin, Search, Ruler } from 'lucide-react';
import {
    FORMATS,
    PRINT_PRICES,
    PRINT_SIZES,
    PRINT_MIN_DPI,
    FRAME_MATERIALS,
    getFrameMaterial,
    getFramePrice,
    PASSEPARTOUT_OPTIONS,
} from '../config/prices';
import { Dialog, DialogContent, DialogTitle } from './dialog';

// Color gradients used to fake a frame look in the live preview and as
// swatches inside the frame picker. Same palette for every material —
// real fine-art frames would have texture, but a flat gradient at small
// sizes communicates the choice clearly enough.
const FRAME_COLOR_GRADIENT = {
    black: ['#1a1a1a', '#3a3a3a', '#0a0a0a'],
    white: ['#e8e8e8', '#fafafa', '#d0d0d0'],
    silver: ['#a8acb0', '#dcdfe2', '#88898d'],
    oak: ['#8b5a2b', '#c08a52', '#6d3f17'],
};

function getFrameStyle(materialId, colorId) {
    if (materialId === 'none' || !colorId) {
        return { background: 'transparent', boxShadow: 'none' };
    }
    const g = FRAME_COLOR_GRADIENT[colorId] || FRAME_COLOR_GRADIENT.black;
    return {
        background: `linear-gradient(135deg, ${g[0]} 0%, ${g[1]} 50%, ${g[2]} 100%)`,
        boxShadow: 'inset 0 0 6px rgba(0,0,0,0.4)',
    };
}

const FRAME_BORDER_PX = 22;

function FramedPreview({ imageUrl, materialId, colorId, passepartoutCm, isPortrait, sideBySide }) {
    const style = getFrameStyle(materialId, colorId);
    const passepartoutPx = passepartoutCm > 0 ? 16 : 0;
    const borderPx = materialId === 'none' ? 0 : FRAME_BORDER_PX;
    const noChrome = materialId === 'none' && passepartoutCm === 0;

    // Image cap depends on layout. Mobile + small-desktop use the
    // shorter cap; only on lg+ (where the side-by-side layout actually
    // kicks in) does the portrait image get the larger budget.
    const imgMaxH = sideBySide
        ? 'max-h-[36vh] lg:max-h-[60vh]'
        : 'max-h-[36vh] sm:max-h-[48vh]';

    // Wrap in a centering flex so the inner block sizes to the image
    // (inline-block won't grow past its content), preventing the
    // bg-white passepartout area from extending beyond the image edges
    // when it's portrait and shorter than the column width.
    return (
        <div className="flex justify-center">
            <div
                style={{
                    ...style,
                    padding: borderPx,
                    maxWidth: '100%',
                    filter: 'drop-shadow(0 12px 22px rgba(0,0,0,0.35))',
                }}
            >
                <div
                    className={noChrome ? '' : 'bg-white'}
                    style={{ padding: passepartoutPx }}
                >
                    {imageUrl ? (
                        <img
                            src={imageUrl}
                            alt="Map preview"
                            className={`block max-w-full ${imgMaxH} w-auto h-auto select-none`}
                            draggable={false}
                        />
                    ) : (
                        <div className="aspect-[4/3] w-full bg-gray-200 flex items-center justify-center text-gray-400 text-xs">
                            No preview
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function ColorSwatch({ colorId, selected, onClick, title }) {
    return (
        <button
            type="button"
            onClick={onClick}
            title={title}
            className={`relative h-7 w-7 rounded-full border-2 transition-all ${selected ? 'border-blue-500 scale-110' : 'border-gray-300 hover:border-gray-500'
                }`}
            style={{
                background:
                    colorId === 'white' ? '#fafafa' :
                        colorId === 'black' ? '#1a1a1a' :
                            colorId === 'silver' ? 'linear-gradient(135deg, #c0c4c8, #8c9094)' :
                                colorId === 'oak' ? 'linear-gradient(135deg, #c08a52, #8b5a2b)' :
                                    '#888',
            }}
        />
    );
}

// Allowed delivery countries with full names for the country picker.
// Codes mirror backend `_PRINT_ALLOWED_COUNTRIES`. The country code
// comes back from Nominatim too — we map ISO-3166 alpha-2 codes (case
// normalised) onto these entries when autofilling.
const SHIPPING_COUNTRIES = [
    { code: 'DE', name: 'Germany' },
    { code: 'AT', name: 'Austria' },
    { code: 'CH', name: 'Switzerland' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'BE', name: 'Belgium' },
    { code: 'LU', name: 'Luxembourg' },
    { code: 'FR', name: 'France' },
    { code: 'IT', name: 'Italy' },
    { code: 'ES', name: 'Spain' },
    { code: 'PT', name: 'Portugal' },
    { code: 'DK', name: 'Denmark' },
    { code: 'SE', name: 'Sweden' },
    { code: 'NO', name: 'Norway' },
    { code: 'FI', name: 'Finland' },
    { code: 'PL', name: 'Poland' },
    { code: 'CZ', name: 'Czechia' },
    { code: 'SK', name: 'Slovakia' },
    { code: 'HU', name: 'Hungary' },
    { code: 'IE', name: 'Ireland' },
    { code: 'GB', name: 'United Kingdom' },
];
const SHIPPING_COUNTRY_CODES = new Set(SHIPPING_COUNTRIES.map(c => c.code));

// Map a Nominatim `address` object onto our shipping fields. Nominatim
// returns OSM's full address tree (road, house_number, postcode, city/
// town/village, country_code …); we collapse it into our shape.
function nominatimToShipping(item) {
    const a = item.address || {};
    const houseNo = a.house_number || '';
    const road = a.road || a.pedestrian || a.path || '';
    const line1 = [road, houseNo].filter(Boolean).join(' ').trim();
    const city = a.city || a.town || a.village || a.municipality || a.hamlet || '';
    const postal = a.postcode || '';
    const countryCode = (a.country_code || '').toUpperCase();
    return {
        line1,
        postal_code: postal,
        city,
        country: SHIPPING_COUNTRY_CODES.has(countryCode) ? countryCode : null,
    };
}

// Debounced Nominatim search. The free public Nominatim instance asks
// for ≤1 req/sec and a meaningful User-Agent — the browser provides one,
// and the 400ms debounce keeps us well under their rate cap.
function AddressLookup({ onPick }) {
    const [q, setQ] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);

    React.useEffect(() => {
        const text = q.trim();
        if (text.length < 3) { setResults([]); return; }
        setLoading(true);
        const t = setTimeout(async () => {
            try {
                const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(text)}&format=json&addressdetails=1&limit=5`;
                const r = await fetch(url, {
                    headers: { 'Accept-Language': 'en' },
                });
                if (!r.ok) throw new Error('lookup failed');
                const data = await r.json();
                setResults(Array.isArray(data) ? data : []);
                setOpen(true);
            } catch (_e) {
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 400);
        return () => clearTimeout(t);
    }, [q]);

    return (
        <div className="relative sm:col-span-2">
            <span className="block text-[11px] font-medium text-gray-600 mb-1">
                Search address
                <span className="ml-1 text-gray-400 font-normal">(autocomplete via OpenStreetMap)</span>
            </span>
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <input
                    type="text"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onFocus={() => results.length > 0 && setOpen(true)}
                    placeholder="e.g. Schmellerstraße 30, München"
                    className="w-full pl-9 pr-9 py-2 text-sm border border-gray-200 rounded-lg bg-white
                        focus:outline-none focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10
                        placeholder:text-gray-400 transition-colors"
                />
                {loading && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
                )}
                {!loading && q && (
                    <button
                        type="button"
                        onClick={() => { setQ(''); setResults([]); setOpen(false); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-700"
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                )}
            </div>
            {open && results.length > 0 && (
                <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {results.map(r => (
                        <button
                            key={r.place_id}
                            type="button"
                            onClick={() => {
                                const filled = nominatimToShipping(r);
                                onPick(filled);
                                setQ(r.display_name);
                                setOpen(false);
                            }}
                            className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 border-b border-gray-100 last:border-0"
                        >
                            {r.display_name}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function isShippingComplete(s) {
    if (!s) return false;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((s.email || '').trim())) return false;
    return ['name', 'line1', 'postal_code', 'city', 'country']
        .every(f => (s[f] || '').toString().trim().length > 0);
}

// Reusable input — same look across the address dialog so individual
// fields don't drift in padding/border/focus styles. Floating label
// sits above to keep the layout compact.
function AddressField({ label, value, onChange, type = 'text', placeholder, required, autoComplete, className = '' }) {
    return (
        <label className={`block ${className}`}>
            <span className="block text-[11px] font-medium text-gray-600 mb-1">
                {label}{required && <span className="text-red-500">*</span>}
            </span>
            <input
                type={type}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                autoComplete={autoComplete}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white
                    focus:outline-none focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10
                    placeholder:text-gray-400 transition-colors"
            />
        </label>
    );
}

function ShippingAddressDialog({ open, onClose, value, onChange }) {
    // Local mirror so typing doesn't bubble keystroke-by-keystroke up
    // through the parent (which would re-render the size cards etc).
    // Committed on Save.
    const [draft, setDraft] = useState(value);
    React.useEffect(() => {
        if (open) setDraft(value);
    }, [open, value]);

    const valid = isShippingComplete(draft);

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md p-6 !rounded-2xl sm:!rounded-2xl">
                <DialogTitle className="text-base font-semibold mb-1">
                    Shipping address
                </DialogTitle>
                <p className="text-[11px] text-gray-500 mb-3">
                    We'll send a confirmation to this email and ship to this address.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1 -mr-1">
                    {/* OpenStreetMap autocomplete — type a street + city
                        and the rest of the form fills itself. */}
                    <AddressLookup
                        onPick={(picked) => {
                            setDraft(s => ({
                                ...s,
                                line1: picked.line1 || s.line1,
                                postal_code: picked.postal_code || s.postal_code,
                                city: picked.city || s.city,
                                country: picked.country || s.country,
                            }));
                        }}
                    />
                    <AddressField
                        label="Full name"
                        required
                        value={draft.name}
                        onChange={(e) => setDraft(s => ({ ...s, name: e.target.value }))}
                        autoComplete="name"
                        className="sm:col-span-2"
                    />
                    <AddressField
                        label="Email"
                        type="email"
                        required
                        value={draft.email}
                        onChange={(e) => setDraft(s => ({ ...s, email: e.target.value }))}
                        autoComplete="email"
                    />
                    <AddressField
                        label="Phone"
                        type="tel"
                        value={draft.phone}
                        onChange={(e) => setDraft(s => ({ ...s, phone: e.target.value }))}
                        autoComplete="tel"
                        placeholder="optional"
                    />
                    <AddressField
                        label="Street + number"
                        required
                        value={draft.line1}
                        onChange={(e) => setDraft(s => ({ ...s, line1: e.target.value }))}
                        autoComplete="address-line1"
                        className="sm:col-span-2"
                    />
                    <AddressField
                        label="Address line 2"
                        value={draft.line2}
                        onChange={(e) => setDraft(s => ({ ...s, line2: e.target.value }))}
                        autoComplete="address-line2"
                        placeholder="optional"
                        className="sm:col-span-2"
                    />
                    <AddressField
                        label="Postal code"
                        required
                        value={draft.postal_code}
                        onChange={(e) => setDraft(s => ({ ...s, postal_code: e.target.value }))}
                        autoComplete="postal-code"
                    />
                    <AddressField
                        label="City"
                        required
                        value={draft.city}
                        onChange={(e) => setDraft(s => ({ ...s, city: e.target.value }))}
                        autoComplete="address-level2"
                    />
                    <label className="block sm:col-span-2">
                        <span className="block text-[11px] font-medium text-gray-600 mb-1">
                            Country<span className="text-red-500">*</span>
                        </span>
                        <select
                            value={draft.country}
                            onChange={(e) => setDraft(s => ({ ...s, country: e.target.value }))}
                            autoComplete="country"
                            className="w-full px-3 py-2 pr-9 text-sm border border-gray-200 rounded-lg bg-white appearance-none cursor-pointer
                                focus:outline-none focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10
                                transition-colors
                                bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 20 20%22 fill=%22%236b7280%22><path d=%22M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z%22/></svg>')] bg-no-repeat bg-[right_0.6rem_center] bg-[length:1.1rem]"
                        >
                            {SHIPPING_COUNTRIES.map(c => (
                                <option key={c.code} value={c.code}>{c.name} ({c.code})</option>
                            ))}
                        </select>
                    </label>
                </div>
                <div className="flex items-center justify-between pt-4">
                    <span className="text-[11px] text-gray-500">
                        {valid ? 'Ready to go.' : 'Required fields marked with *'}
                    </span>
                    <Button
                        variant="black"
                        size="sm"
                        onClick={() => { onChange(draft); onClose(); }}
                        disabled={!valid}
                    >
                        <Check className="h-4 w-4 mr-1" />
                        Save address
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// Picker buttons (Size / Frame / Passepartout / Shipping) all share
// this shape so the rows in the main print panel line up visually.
// `complete` flips to the emerald-checkmark look.
function PickerButton({ icon: Icon, title, subtitle, complete, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`w-full flex items-center gap-2 px-3 py-2.5 border rounded-xl transition-colors text-left ${
                complete
                    ? 'bg-emerald-50 border-emerald-300 hover:bg-emerald-100'
                    : 'bg-white border-gray-200 hover:bg-gray-50'
            }`}
        >
            {Icon && (
                <Icon className={`h-4 w-4 flex-shrink-0 ${complete ? 'text-emerald-600' : 'text-gray-500'}`} />
            )}
            <div className="flex-1 min-w-0">
                <div className="text-sm font-medium leading-tight">{title}</div>
                {subtitle && (
                    <div className="text-[11px] text-gray-600 truncate">{subtitle}</div>
                )}
            </div>
            {complete ? (
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-emerald-500 flex items-center justify-center">
                    <Check className="h-4 w-4 text-white" />
                </div>
            ) : (
                <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
            )}
        </button>
    );
}

// Size picker dialog. The grid lives here now (was inline in the print
// panel) so the main panel stays compact.
function SizeSelectionDialog({
    open, onClose, selectedSize, onChange,
    availableSizes, getSizeDimensions,
}) {
    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md p-5 !rounded-2xl sm:!rounded-2xl">
                <DialogTitle className="text-base font-semibold mb-3">
                    Choose a size
                </DialogTitle>
                <div className="grid grid-cols-2 gap-2.5 pr-1.5 -mr-1.5">
                    {PRINT_SIZES.map(size => {
                        const dim = getSizeDimensions(size);
                        const isAvailable = availableSizes[size] && dim;
                        const isDisabled = !isAvailable;
                        const isSelected = selectedSize === size;
                        const price = PRINT_PRICES[size] || 0;
                        return (
                            <button
                                key={size}
                                disabled={isDisabled}
                                onClick={() => { onChange(size); onClose(); }}
                                className={`p-3 border rounded-xl text-left transition-colors shadow-sm
                                    ${isSelected
                                        ? 'bg-gray-900 text-white border-gray-900 shadow-md'
                                        : 'bg-white text-gray-900 border-gray-200 hover:bg-gray-50'}
                                    ${isDisabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                            >
                                <div className="font-semibold text-base">{size}</div>
                                <div className={`text-xs ${isSelected ? 'text-gray-300' : 'text-gray-500'}`}>
                                    {dim ? `${dim.width}x${dim.height} cm` : '—'}
                                </div>
                                <div className={`text-sm font-medium mt-1 ${isSelected ? 'text-white' : 'text-gray-900'}`}>
                                    €{price.toFixed(2)}
                                </div>
                                {isDisabled && (
                                    <div className="text-[10px] text-orange-500 mt-1 flex items-center gap-1">
                                        N/A <Info className="h-3 w-3" />
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>
                <p className="text-[11px] text-gray-500 mt-3">
                    Some sizes may be unavailable if the rendered map doesn't have enough resolution for that print size.
                </p>
            </DialogContent>
        </Dialog>
    );
}

// Voucher row used inside the summary dialog. Drives a shared voucher
// controller passed down from the parent so the discount state lines
// up with the digital tab too.
function VoucherRow({ voucher }) {
    if (!voucher) return null;
    const {
        code, setCode, discount, discountType, isApplying, errorMsg, setErrorMsg,
        buttonState, apply, clear, getApplyButtonStyles,
    } = voucher;
    return (
        <div>
            <div className="flex flex-col items-center">
                {discount ? (
                    <div className="text-green-600 font-normal mb-1 text-center text-sm">
                        {discountType === 'percent'
                            ? `${discount}% discount applied`
                            : `€${(discount / 100).toFixed(2)} discount applied`}
                    </div>
                ) : (
                    <div className="text-[11px] text-gray-500 mb-1">
                        Have a voucher? Apply it here.
                    </div>
                )}
            </div>
            <div className="flex justify-center items-center w-full">
                <div className="flex gap-2 items-center w-full max-w-xs">
                    <div className="relative flex-1">
                        <Input
                            type="text"
                            placeholder="Voucher code"
                            value={code}
                            onChange={(e) => {
                                setCode(e.target.value);
                                if (errorMsg) setErrorMsg(null);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && code && !isApplying && !discount) apply();
                            }}
                            className={`w-full bg-gray-300/30 rounded-xl text-center focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-gray-400 text-sm transition-colors ${
                                errorMsg ? 'border border-red-500 placeholder:text-red-400' : 'border-0'
                            }`}
                        />
                        {code && (
                            <button
                                onClick={clear}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                    <Button
                        onClick={apply}
                        disabled={!code || isApplying || discount}
                        size="icon"
                        variant="black"
                        className={`relative h-9 w-9 transition-colors ${getApplyButtonStyles ? getApplyButtonStyles() : ''}`}
                    >
                        {isApplying
                            ? <Loader2 className="h-4 w-4 animate-spin" />
                            : buttonState === 'error'
                                ? <X className="h-4 w-4" />
                                : <Check className="h-4 w-4" />}
                    </Button>
                </div>
            </div>
            {errorMsg && (
                <p className="mt-1 text-xs text-red-500 text-center" role="alert">
                    {errorMsg}
                </p>
            )}
        </div>
    );
}

function PassepartoutDialog({ open, onClose, value, onChange }) {
    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-xs p-5 sm:rounded-xl">
                <DialogTitle className="text-base font-semibold mb-1">
                    Passepartout
                </DialogTitle>
                <p className="text-[11px] text-gray-500 mb-3">
                    A white mat that sits between the print and the frame.
                </p>
                <div className="space-y-2">
                    {PASSEPARTOUT_OPTIONS.map(opt => {
                        const selected = opt.value === value;
                        return (
                            <button
                                key={opt.value}
                                onClick={() => { onChange(opt.value); onClose(); }}
                                className={`w-full flex items-center justify-between p-3 border rounded-xl text-left transition-colors ${selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                                    }`}
                            >
                                <span className="text-sm font-medium">{opt.label}</span>
                                {selected && <Check className="h-4 w-4 text-blue-600" />}
                            </button>
                        );
                    })}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function FrameSelectionDialog({ open, onClose, materialId, colorId, onChange, size }) {
    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md p-5 !rounded-2xl sm:!rounded-2xl">
                <DialogTitle className="text-base font-semibold mb-1">
                    Choose a frame
                </DialogTitle>
                <p className="text-[11px] text-gray-500 mb-3">
                    Frame previews are illustrative — actual frames are
                    high-quality fine-art frames from our print partner.
                </p>
                {/* pr-1.5 -mr-1.5 leaves room for the scrollbar AND keeps
                    the option cards' rounded right edges away from the
                    dialog's rounded corners. */}
                <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1.5 -mr-1.5">
                    {FRAME_MATERIALS.map(mat => {
                        const isSelected = mat.id === materialId;
                        const price = getFramePrice(mat.id, size);
                        const previewColor = mat.colors[0]?.id || null;
                        return (
                            <div
                                key={mat.id}
                                className={`p-2 lg:p-3 border rounded-2xl transition-colors ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                                    }`}
                            >
                                <button
                                    type="button"
                                    onClick={() => {
                                        const nextColor = mat.colors.length > 0
                                            ? (mat.colors.find(c => c.id === colorId)?.id || mat.colors[0].id)
                                            : null;
                                        onChange(mat.id, nextColor);
                                        if (mat.colors.length <= 1) onClose();
                                    }}
                                    className="w-full flex items-center gap-3 text-left pr-3"
                                >
                                    <div
                                        className="w-14 h-10 rounded-sm flex-shrink-0"
                                        style={getFrameStyle(mat.id, previewColor)}
                                    >
                                        <div className="w-full h-full bg-white/80 m-1" style={{ width: 'calc(100% - 8px)', height: 'calc(100% - 8px)' }} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-sm">{mat.label}</div>
                                        <div className="text-xs text-gray-500">
                                            {mat.id === 'none' ? 'Included' : `+€${price.toFixed(2)} for Size ${size}`}
                                        </div>
                                    </div>
                                    {isSelected && <Check className="h-4 w-4 text-blue-600 flex-shrink-0" />}
                                </button>
                                {isSelected && mat.colors.length > 1 && (
                                    <div className="flex gap-2 mt-3 pl-[68px]">
                                        {mat.colors.map(c => (
                                            <ColorSwatch
                                                key={c.id}
                                                colorId={c.id}
                                                selected={colorId === c.id}
                                                title={c.label}
                                                onClick={() => onChange(mat.id, c.id)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                <div className="flex justify-end pt-3">
                    <Button variant="black" size="sm" onClick={onClose}>Done</Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// Final review step. Shows everything the customer has picked plus the
// itemised cost (including a guess at the shipping rate from the
// shipping country), exposes the voucher input, and triggers the
// actual order placement.
function OrderSummaryDialog({
    open, onClose, onConfirm, isUnlocking,
    finalImage, materialId, colorId, passepartoutCm,
    size, sizeDimensions,
    printPrice, framePrice, voucher, applyVoucher,
    isFree, shippingComplete, freeShipping,
    onOpenShippingDialog,
    estShippingCents,
}) {
    const material = getFrameMaterial(materialId);
    const subtotal = printPrice + framePrice;
    const discounted = applyVoucher(subtotal);
    const discountAmount = subtotal - discounted;
    const shipping = isFree ? 0 : estShippingCents / 100;
    const total = discounted + shipping;

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md p-5 !rounded-2xl sm:!rounded-2xl">
                <DialogTitle className="text-base font-semibold mb-3">
                    Order summary
                </DialogTitle>

                <div className="flex gap-3 items-start mb-3">
                    <div className="w-24 flex-shrink-0">
                        <FramedPreview
                            imageUrl={finalImage}
                            materialId={materialId}
                            colorId={colorId}
                            passepartoutCm={passepartoutCm}
                            isPortrait={false}
                            sideBySide={false}
                        />
                    </div>
                    <div className="flex-1 min-w-0 text-sm space-y-0.5">
                        <div className="font-semibold">
                            Size {size}
                            {sizeDimensions && (
                                <span className="text-gray-500 font-normal">
                                    {' '}({sizeDimensions.width}×{sizeDimensions.height} cm)
                                </span>
                            )}
                        </div>
                        <div className="text-gray-700">
                            {material.label}
                            {colorId ? ` — ${colorId}` : ''}
                        </div>
                        <div className="text-gray-700">
                            Passepartout: {passepartoutCm} cm
                        </div>
                        {isFree && shippingComplete && (
                            <div className="text-gray-500 text-[11px] mt-1 truncate">
                                Ship to: {freeShipping.name}, {freeShipping.city}
                            </div>
                        )}
                    </div>
                </div>

                {/* Shipping picker — free orders only, since Stripe
                    collects the address for paid orders. Re-uses the
                    standalone ShippingAddressDialog (opened from the
                    parent so the dialogs nest cleanly). */}
                {isFree && (
                    <div className="border-t border-gray-200 pt-3 mb-3">
                        <div className="text-xs font-medium text-gray-700 mb-1.5">Shipping address</div>
                        <PickerButton
                            icon={MapPin}
                            title={shippingComplete ? freeShipping.name : 'Enter shipping address'}
                            subtitle={
                                shippingComplete
                                    ? `${freeShipping.line1}, ${freeShipping.postal_code} ${freeShipping.city}, ${freeShipping.country}`
                                    : 'Name, email, address, country'
                            }
                            complete={shippingComplete}
                            onClick={onOpenShippingDialog}
                        />
                    </div>
                )}

                {/* Voucher input — shared controller from the parent */}
                <div className="border-t border-gray-200 pt-3 mb-3">
                    <VoucherRow voucher={voucher} />
                </div>

                {/* Cost breakdown */}
                <div className="border-t border-gray-200 pt-3 mb-3 space-y-1 text-sm">
                    <div className="flex justify-between text-gray-700">
                        <span>Print (Size {size})</span>
                        <span>€{printPrice.toFixed(2)}</span>
                    </div>
                    {framePrice > 0 && (
                        <div className="flex justify-between text-gray-700">
                            <span>Frame</span>
                            <span>€{framePrice.toFixed(2)}</span>
                        </div>
                    )}
                    {discountAmount > 0 && (
                        <div className="flex justify-between text-green-600">
                            <span>Voucher discount</span>
                            <span>−€{discountAmount.toFixed(2)}</span>
                        </div>
                    )}
                    <div className="flex justify-between text-gray-700">
                        <span>Shipping</span>
                        <span>{isFree ? 'Free' : `€${shipping.toFixed(2)}`}</span>
                    </div>
                    <div className="flex justify-between items-baseline pt-2 border-t border-gray-100">
                        <span className="font-semibold">Total</span>
                        <span className="text-lg font-semibold">€{total.toFixed(2)}</span>
                    </div>
                </div>

                <div className="flex flex-col items-center">
                    <Button
                        variant="black"
                        onClick={onConfirm}
                        disabled={isUnlocking || (isFree && !shippingComplete)}
                        className="w-full text-sm sm:text-base"
                    >
                        {isUnlocking ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Processing...
                            </>
                        ) : (
                            <>
                                <Check className="mr-2 h-4 w-4" />
                                {isFree ? 'Place Order' : 'Continue to payment'}
                            </>
                        )}
                    </Button>
                    <p className="text-[11px] text-gray-500 text-center mt-2 max-w-xs px-2">
                        {isFree
                            ? (shippingComplete
                                ? 'Free order — no shipping charge. EU delivery within 7–10 business days.'
                                : 'Add a shipping address above to place the order.')
                            : 'You\'ll confirm shipping address and pay on the next step.'}
                    </p>
                </div>
            </DialogContent>
        </Dialog>
    );
}

const MapPrintSection = ({
    finalImage,
    maxDpis,
    selectedFormat,
    onPrintSelect,
    isUnlocking,
    isPortrait,
    voucher,
}) => {
    const [selectedSize, setSelectedSize] = useState('S');
    const [frameMaterial, setFrameMaterial] = useState('none');
    const [frameColor, setFrameColor] = useState(null);
    const [passepartoutCm, setPassepartoutCm] = useState(0);
    const [sizeDialogOpen, setSizeDialogOpen] = useState(false);
    const [frameDialogOpen, setFrameDialogOpen] = useState(false);
    const [passepartoutDialogOpen, setPassepartoutDialogOpen] = useState(false);
    const [shippingDialogOpen, setShippingDialogOpen] = useState(false);
    const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);

    const hasFrame = frameMaterial !== 'none';

    // Passepartout only makes sense with a frame around it (the mat sits
    // between the print and the frame). If the user clears the frame,
    // snap back to 0 cm.
    React.useEffect(() => {
        if (!hasFrame && passepartoutCm !== 0) setPassepartoutCm(0);
    }, [hasFrame, passepartoutCm]);

    // Free-tier (voucher-zeroed) print orders skip Stripe so we have to
    // collect shipping ourselves. Inline form below; only renders when
    // the discounted total is 0.
    const [freeShipping, setFreeShipping] = useState({
        name: '',
        email: '',
        phone: '',
        line1: '',
        line2: '',
        postal_code: '',
        city: '',
        country: 'DE',
    });
    const [freeShippingError, setFreeShippingError] = useState(null);

    const getSizeDimensions = (size) => {
        const raw = FORMATS[selectedFormat.value]?.[size];
        if (!raw) return null;
        const [width, height] = raw.split('x').map(d => parseFloat(d.replace(',', '.')));
        return { width, height };
    };

    // Print quality floor is higher than digital — paper has no zoom-out.
    const availableSizes = useMemo(() => {
        const out = {};
        PRINT_SIZES.forEach(size => {
            const dim = getSizeDimensions(size);
            if (!dim) { out[size] = false; return; }
            const dpi = (maxDpis && maxDpis[size]) || 0;
            out[size] = dpi >= PRINT_MIN_DPI;
        });
        return out;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [maxDpis, selectedFormat]);

    React.useEffect(() => {
        if (!availableSizes[selectedSize]) {
            const fallback = PRINT_SIZES.find(s => availableSizes[s]);
            if (fallback) setSelectedSize(fallback);
        }
    }, [availableSizes, selectedSize]);

    const material = getFrameMaterial(frameMaterial);
    const framePrice = getFramePrice(frameMaterial, selectedSize);
    const printPrice = PRINT_PRICES[selectedSize] || 0;
    const subtotal = printPrice + framePrice;

    // Voucher discount applies to the print + frame subtotal (not
    // shipping). Voucher state lives in the parent (so it stays
    // synced with the digital tab); we just read it here.
    const voucherDiscountVal = voucher?.discount || 0;
    const voucherDiscountType = voucher?.discountType;
    const applyVoucher = (amount) => {
        if (!voucherDiscountVal) return amount;
        if (voucherDiscountType === 'percent') {
            return amount * (1 - voucherDiscountVal / 100);
        }
        if (voucherDiscountType === 'amount') {
            return Math.max(0, amount - voucherDiscountVal / 100);
        }
        return amount;
    };

    const discountedSubtotal = applyVoucher(subtotal);

    // Estimated shipping based on the shipping address's country. We can
    // only really know once Stripe collects the address (paid flow), so
    // this is purely for display in the summary; the actual rate the
    // customer pays is whichever shipping_option they pick at checkout.
    const estShippingCents = freeShipping.country === 'DE' ? 999 : 1599;
    const totalPrice = discountedSubtotal + (discountedSubtotal <= 0 ? 0 : estShippingCents / 100);

    const isFree = discountedSubtotal <= 0;
    const shippingComplete = isShippingComplete(freeShipping);

    const handlePurchase = async () => {
        const dimensions = getSizeDimensions(selectedSize);
        if (!dimensions) return;
        if (isPortrait) {
            [dimensions.width, dimensions.height] = [dimensions.height, dimensions.width];
        }

        // Free orders need shipping info (Stripe Checkout can't collect
        // it without a charge). Validation happens inside the dialog —
        // here we just refuse to submit if it's still incomplete.
        let shipping = null;
        if (isFree) {
            if (!shippingComplete) {
                setFreeShippingError('Please complete the shipping address.');
                setShippingDialogOpen(true);
                return;
            }
            setFreeShippingError(null);
            shipping = { ...freeShipping };
        }

        await onPrintSelect({
            size: selectedSize,
            dimensions,
            frame: frameMaterial,
            frameColor,
            passepartoutCm,
            voucher: voucher?.code || null,
            shipping,
            email: shipping?.email,
        });
    };

    // Open the summary dialog. The shipping address picker lives
    // inside the summary now (for free orders), so we don't block
    // here on it — the summary's Place Order button is what enforces
    // it being filled in.
    const handleContinueToSummary = () => {
        setFreeShippingError(null);
        setSummaryDialogOpen(true);
    };

    const hasUnavailableSizes = PRINT_SIZES.some(s => !availableSizes[s]);

    // Desktop layout: portrait map → image left / options right; landscape
    // → stacked. Mobile is always stacked.
    const sideBySide = isPortrait;

    const previewBlock = (
        <div className="py-1 sm:py-2">
            <FramedPreview
                imageUrl={finalImage}
                materialId={frameMaterial}
                colorId={frameColor}
                passepartoutCm={passepartoutCm}
                isPortrait={isPortrait}
                sideBySide={sideBySide}
            />
            <p className="mt-1.5 sm:mt-3 text-[11px] sm:text-xs text-gray-500 text-center">
                Printed on premium <span className="font-medium text-gray-700">Hahnemühle</span> fine-art paper.
            </p>
        </div>
    );

    // Selected size summary for the Size picker's subtitle line.
    const selectedDim = getSizeDimensions(selectedSize);
    const selectedSizeSubtitle = selectedDim
        ? `${selectedDim.width}×${selectedDim.height} cm · €${printPrice.toFixed(2)}`
        : `€${printPrice.toFixed(2)}`;
    const frameSubtitle = `${material.label}${frameColor ? ` — ${frameColor}` : ''}${framePrice > 0 ? ` (+€${framePrice.toFixed(2)})` : ''}`;

    const choicesBlock = (
        <div className="space-y-2 sm:space-y-2.5">
            {hasUnavailableSizes && (
                <div className="flex items-center justify-center p-1.5 bg-yellow-100 border border-yellow-300 rounded-xl">
                    <AlertTriangle className="h-3.5 w-3.5 text-red-500 mr-1.5 flex-shrink-0" />
                    <p className="text-[11px] sm:text-xs text-red-500 text-center">
                        Some sizes need a larger area for print quality.
                    </p>
                </div>
            )}

            {/* All picker buttons share the same shape so the choice
                rows feel like one cohesive list. */}
            <PickerButton
                icon={Ruler}
                title={`Size ${selectedSize}`}
                subtitle={selectedSizeSubtitle}
                complete={!!availableSizes[selectedSize]}
                onClick={() => setSizeDialogOpen(true)}
            />
            <PickerButton
                icon={FrameIcon}
                title="Material & Frame"
                subtitle={frameSubtitle}
                complete={hasFrame}
                onClick={() => setFrameDialogOpen(true)}
            />
            {hasFrame && (
                <PickerButton
                    icon={null}
                    title={`Passepartout: ${passepartoutCm} cm`}
                    subtitle="White mat between print and frame"
                    complete={false}
                    onClick={() => setPassepartoutDialogOpen(true)}
                />
            )}
            {/* Shipping picker moved into the summary dialog — keeps
                the main panel focused on the print configuration and
                hides shipping from paid flows (where Stripe collects
                the address). */}
        </div>
    );

    // Total + Continue button. The total already includes (estimated)
    // shipping so the customer sees what they'll actually pay before
    // they go through the summary step.
    const summaryBlock = (
        <div className="flex flex-col items-center pt-1">
            <div className="text-xs text-gray-500">
                Print €{printPrice.toFixed(2)}
                {framePrice > 0 && ` + Frame €${framePrice.toFixed(2)}`}
                {!isFree && ` + Shipping €${(estShippingCents / 100).toFixed(2)}`}
                {voucherDiscountVal > 0 && voucherDiscountType === 'percent' && ` − ${voucherDiscountVal}%`}
                {voucherDiscountVal > 0 && voucherDiscountType === 'amount' && ` − €${(voucherDiscountVal / 100).toFixed(2)}`}
            </div>
            <div className="text-base sm:text-lg font-semibold text-gray-900 mb-1">
                Total €{totalPrice.toFixed(2)}
            </div>
            <p className="text-[11px] text-gray-500 mb-2 text-center max-w-xs">
                Vouchers can be applied at the summary step.
            </p>
            <Button
                variant="black"
                onClick={handleContinueToSummary}
                disabled={!availableSizes[selectedSize] || isUnlocking}
                className="w-auto text-sm sm:text-base"
            >
                <Check className="mr-2 h-4 w-4" />
                Continue to summary
            </Button>
            <p className="text-[10px] sm:text-[11px] text-gray-500 text-center mt-1.5 max-w-xs px-2">
                Review your order on the next step before placing it.
            </p>
        </div>
    );

    return (
        <>
            <div className="space-y-2 sm:space-y-3">
                {/* Image + choices. Side-by-side only kicks in at lg+
                    where there's actually room for two columns. */}
                <div className={
                    sideBySide
                        ? 'flex flex-col lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-5 gap-2 lg:items-center'
                        : 'flex flex-col gap-2 sm:gap-3'
                }>
                    {previewBlock}
                    {choicesBlock}
                </div>
                {summaryBlock}
            </div>

            <SizeSelectionDialog
                open={sizeDialogOpen}
                onClose={() => setSizeDialogOpen(false)}
                selectedSize={selectedSize}
                onChange={setSelectedSize}
                availableSizes={availableSizes}
                getSizeDimensions={getSizeDimensions}
            />

            <FrameSelectionDialog
                open={frameDialogOpen}
                onClose={() => setFrameDialogOpen(false)}
                materialId={frameMaterial}
                colorId={frameColor}
                size={selectedSize}
                onChange={(m, c) => { setFrameMaterial(m); setFrameColor(c); }}
            />

            <PassepartoutDialog
                open={passepartoutDialogOpen}
                onClose={() => setPassepartoutDialogOpen(false)}
                value={passepartoutCm}
                onChange={setPassepartoutCm}
            />

            <ShippingAddressDialog
                open={shippingDialogOpen}
                onClose={() => setShippingDialogOpen(false)}
                value={freeShipping}
                onChange={(next) => { setFreeShipping(next); setFreeShippingError(null); }}
            />

            <OrderSummaryDialog
                open={summaryDialogOpen}
                onClose={() => setSummaryDialogOpen(false)}
                onConfirm={async () => { await handlePurchase(); }}
                isUnlocking={isUnlocking}
                finalImage={finalImage}
                materialId={frameMaterial}
                colorId={frameColor}
                passepartoutCm={passepartoutCm}
                size={selectedSize}
                sizeDimensions={selectedDim}
                printPrice={printPrice}
                framePrice={framePrice}
                voucher={voucher}
                applyVoucher={applyVoucher}
                isFree={isFree}
                shippingComplete={shippingComplete}
                freeShipping={freeShipping}
                onOpenShippingDialog={() => setShippingDialogOpen(true)}
                estShippingCents={estShippingCents}
            />
        </>
    );
};

export default MapPrintSection;
